<template>
  <b-row>
    <b-col>
      <b-card>
        <div class="mt-1 d-flex justify-content-center">
          <b-col
            cols="12"
            lg="4"
          >
            <b-form-input
              ref="containerCode"
              v-model="containerCode"
              :disabled="loading"
              class="mb-1"
              placeholder="Scan container code"
              @keydown.enter="searchContainer(containerCode)"
            />
          </b-col>
        </div>
        <p
          v-if="!loading && latestStatus"
          class="text-center"
        >
          <feather-icon
            :icon="latestStatus === 'success' ? 'CheckCircleIcon' : 'XCircleIcon'"
            class="cursor-pointer font-large-2"
            :class="`text-${latestStatus}`"
          />
        </p>
        <p
          v-if="!loading && latestText"
          class="latest-text text-center"
          v-html="latestText"
        />
      </b-card>
      <b-card>
        <h5>Scanning shipments</h5>
        <ul>
          <li
            v-for="(shipment) in shipmentsData"
            :key="shipment.id"
          >
            From
            <strong>
              <a @click="shipment.showInfo = !shipment.showInfo">
                {{ shipment.document_number }}<feather-icon icon="InfoIcon" />
              </a>
            </strong> arrived
            <strong>
              {{ Array.isArray(shipment.arrivedContainers) ? shipment.arrivedContainers.length : 'X' }}
            </strong> out of
            <strong>
              {{ shipment.bags }}
            </strong>
            <div v-if="shipment.showInfo">
              {{ Array.isArray(shipment.notArrivedContainers) ? 'Not scanned containers: ' + shipment.notArrivedContainers.map(container => container.code).join(', ') : '' }}
            </div>
          </li>
        </ul>
      </b-card>
      <b-card>
        <h5>Your scan history</h5>
        <ul>
          <li
            v-for="(entry, index) in [...history].reverse()"
            :key="index"
          >
            <feather-icon
              :icon="entry.status === 'success' ? 'CheckCircleIcon' : 'XCircleIcon'"
              class="cursor-pointer"
              :class="`text-${entry.status}`"
            />
            {{ entry.text }}
          </li>
        </ul>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BFormInput,
} from 'bootstrap-vue'
import handleError from '@/views/components/errorHandler'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BFormInput,
  },
  data() {
    return {
      containerCode: null,
      loading: false,
      latestStatus: null,
      latestText: null,
      history: [],
      watchedShipments: [],
      shipmentsData: [],
    }
  },
  mounted() {
    this.$refs.containerCode.focus()
  },
  methods: {
    searchContainer(code) {
      this.loading = true
      this.$http.get(
        '/v1/containers/by-code',
        {
          params: {
            code: code,
          },
        },
      )
        .then(response => {
          const containersCount = response.data.data.length
          if (containersCount === 1) {
            const container = response.data.data[0]
            this.arriveContainer(container)
            this.fetchShipmentInformation(container)
          } else if (containersCount === 0) {
            this.failure(`Container "${code}" not found!`)
          } else {
            this.failure(`${containersCount} containers found with the code "${code}"!`)
          }
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.failure(error.response.data.message, error.response.data.type)
          } else {
            this.failure()
          }
        })
    },
    arriveContainer(container) {
      this.loading = true
      this.$http.post(
        `/v1/containers/${container.id}/arrival`,
      )
        .then(() => {
          this.success(`Container ${container.code} arrived successfully!`)
          this.refreshWaybillsStatistics()
        })
        .catch(error => {
          if (error.response.status === 419) {
            this.failure(error.response.data.message, error.response.data.type)
            this.refreshWaybillsStatistics()
          } else if (error.response.status === 404) {
            this.failure(error.response.data.message, error.response.data.type)
          } else {
            this.failure()
          }
        })
    },
    fetchShipmentInformation(container) {
      for (let i = 0; i < this.shipmentsData.length; i += 1) {
        if (this.shipmentsData[i].id === container.shipment_id) {
          return
        }
      }

      this.$http.get(
        `/v1/companies/${this.$activeCompany().data.company.id}/shipments/${container.shipment_id}`,
      )
        .then(response => {
          const shipmentData = response.data
          shipmentData.showInfo = false
          shipmentData.arrivedContainers = null
          shipmentData.notArrivedContainers = null
          this.shipmentsData.push(shipmentData)
          this.refreshWaybillsStatistics()
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
    },
    refreshWaybillsStatistics() {
      for (let i = 0; i < this.shipmentsData.length; i += 1) {
        this.$http.get(
          '/v1/api/events',
          {
            params: {
              document_number: this.shipmentsData[i].document_number,
              type: 'arrived_to_facility',
              reference_type: 'container',
            },
          },
        )
          .then(response => {
            this.shipmentsData[i].arrivedContainers = [...response.data.data]
          })
          .catch(error => {
            this.failure()
            handleError(error, this.$toast)
          })
        this.$http.get(
          `/v1/api/companies/${this.$activeCompany().data.company.id}/shipments/${this.shipmentsData[i].id}/events/arrived_to_facility`,
        )
          .then(response => {
            this.shipmentsData[i].notArrivedContainers = [...response.data.data]
          })
          .catch(error => {
            this.failure()
            handleError(error, this.$toast)
          })
      }
    },
    async success(text) {
      this.pushStatus('success', text)
      const audio = new Audio('/sounds/success.mp3')
      await audio.play()
      this.loading = false
      await this.$nextTick()
      this.$refs.containerCode.select()
    },
    async failure(text, status) {
      text = text || 'System error'
      status = status || 'danger'
      this.pushStatus(status, text)
      const audio = new Audio('/sounds/failure.mp3')
      await audio.play()
      this.loading = false
      await this.$nextTick()
      this.$refs.containerCode.select()
    },
    pushStatus(status, text) {
      this.latestStatus = status
      this.latestText = text
      this.history.push({
        status: this.latestStatus,
        text: this.latestText,
      })
    },
  },
}
</script>

<style lang="scss">
html[dir] body .app-content {
  padding: calc(1.3rem) calc(2rem - 0.8rem) 0 calc(2rem - 0.8rem) !important;
}
ul, ol {
  list-style: none;
  padding: 0;
  margin: 0;
}
</style>
